// Refund a paid POS bill → ZATCA credit note (server: POST /cashier/orders/{id}/refund). Manager PIN + reason required.
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const METHODS = {
  cash: 'Cash from drawer',
  card: 'Card (complete on the card terminal)',
  bank_transfer: 'Bank transfer',
  credit: 'Customer credit account',
};
const NOTES = {
  cash: 'Hand the cash back now — it reduces expected cash at shift close.',
  card: 'The credit note does NOT return the money. Complete the card refund on the card terminal.',
  bank_transfer: 'The credit note does NOT return the money. Transfer it to the customer separately.',
  credit: 'The amount is removed from the customer’s credit balance.',
};

export default function RefundOrderDialog({ open, onOpenChange, order, onRefunded, currencySymbol = 'SAR' }) {
  const orig = String(order?.payment_method || 'cash').toLowerCase();
  const allowed = Object.keys(METHODS).filter((m) => m !== 'credit' || orig === 'credit');
  const [method, setMethod] = useState('cash');
  const [reason, setReason] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setMethod(allowed.includes(orig) ? orig : 'cash'); setReason(''); setPin(''); setBusy(false); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setBusy(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('cashier_token')}` };
      const { data } = await api.post(`/cashier/orders/${order.id}/refund`,
        { reason: reason.trim(), manager_pin: pin, refund_method: method }, { headers });
      toast.success(`${data.message}. ${data.refund_settlement?.note || ''}`, { duration: 8000 });
      onRefunded?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="refund-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Undo2 size={18} /> Refund bill #{order?.order_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">Full refund of {currencySymbol} {Number(order?.total || 0).toFixed(2)} (paid by {orig}). A ZATCA credit note is issued.</p>
          <div>
            <Label>Return money by</Label>
            <div className="grid grid-cols-1 gap-1 mt-1">
              {allowed.map((m) => (
                <label key={m} className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer ${method === m ? 'border-primary bg-primary/5' : ''}`} data-testid={`refund-method-${m}`}>
                  <input type="radio" name="refund-method" checked={method === m} onChange={() => setMethod(m)} /> {METHODS[m]}
                </label>
              ))}
            </div>
            <p className={`text-xs mt-1 ${method === 'cash' || method === 'credit' ? 'text-muted-foreground' : 'text-amber-700'}`} data-testid="refund-method-note">{NOTES[method]}</p>
          </div>
          <div>
            <Label htmlFor="refund-reason">Reason</Label>
            <Textarea id="refund-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this bill refunded?" data-testid="refund-reason" />
          </div>
          <div>
            <Label htmlFor="refund-pin">Manager PIN</Label>
            <Input id="refund-pin" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} data-testid="refund-pin" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="refund-cancel-btn">Cancel</Button>
            <Button variant="destructive" className="flex-1" disabled={busy || !reason.trim() || pin.length < 4} onClick={submit} data-testid="refund-confirm-btn">
              {busy ? 'Refunding…' : 'Issue refund'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
